import * as THREE from 'three';
import { DUNGEON, MAZE } from '@somnhold/content';
import { simToWorld } from '@somnhold/render';
import { hexToWorld, ropeAt, type BattleState, type Command, type Hero, type Maze } from '@somnhold/sim';
import { t } from '../i18n.ts';
import { h, setFill, viewH, viewW } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';
import { GoalFold } from './goal-fold.ts';
import { PartyFrames } from './party-frames.ts';

/** A chevron pointing down; turned to point at a place off screen. */
const CHEVRON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 8l7 8 7-8"/></svg>';

const secs = (ticks: number): number => Math.max(0, Math.ceil(ticks / 12));
const clock = (n: number): string => `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`;
const near = (a: { x: number; y: number }, b: { x: number; y: number }, r: number): boolean => (a.x - b.x) ** 2 + (a.y - b.y) ** 2 <= r * r;
const at = (m: Maze, i: number): { x: number; y: number } => {
  const n = m.layout.nodes[i] as { q: number; r: number };
  return hexToWorld(n.q, n.r);
};
/** The map frames at least this much around what is known (world units): about six hexes. */
const MAP_MIN_EXT = 6000;
/** The «?» has been opened once on this device. */
const HOW_SEEN = 'somnhold.maze.how';
/** The prowler is "near" this far (world units): about five crossings. */
const PROWL_NEAR = 9000;

type Way = { x: number; y: number; label: string; danger?: boolean; /** A friend on the floor, to go and lift. */ ally?: boolean; /** Only pointed at from the screen's edge (the centre's way). */ edgeOnly?: boolean };

/** The map's marks, drawn the same on the canvas and in its legend. */
const MARK = { me: '#ffffff', ally: '#7fd36a', down: '#ff4f3a', lever: '#ff4f3a', leverDone: '#ff8a6e', camp: '#8a8d96', campDone: '#ff4f3a', chest: '#eceef2', chestDone: '#6b6f78', entry: '#9fb8d0' } as const;
type Shape = 'dot' | 'ring' | 'cross' | 'square' | 'tent' | 'diamond';

const mark = (ctx: CanvasRenderingContext2D, shape: Shape, x: number, y: number, r: number, color: string): void => {
  ctx.fillStyle = color;
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(2, r * 0.45);
  ctx.beginPath();
  if (shape === 'dot') ctx.arc(x, y, r, 0, Math.PI * 2);
  else if (shape === 'ring') ctx.arc(x, y, r, 0, Math.PI * 2);
  else if (shape === 'square') ctx.rect(x - r * 0.8, y - r * 0.8, r * 1.6, r * 1.6);
  else if (shape === 'diamond') {
    ctx.moveTo(x, y - r);
    ctx.lineTo(x + r, y);
    ctx.lineTo(x, y + r);
    ctx.lineTo(x - r, y);
    ctx.closePath();
  } else if (shape === 'tent') {
    ctx.moveTo(x, y - r);
    ctx.lineTo(x + r, y + r * 0.8);
    ctx.lineTo(x - r, y + r * 0.8);
    ctx.closePath();
  } else {
    ctx.moveTo(x - r, y - r);
    ctx.lineTo(x + r, y + r);
    ctx.moveTo(x + r, y - r);
    ctx.lineTo(x - r, y + r);
    ctx.stroke();
    return;
  }
  if (shape === 'ring') ctx.stroke();
  else ctx.fill();
};

/** One line of the legend: its mark, as on the map, and what it is. */
const legendItem = (shape: Shape, color: string, label: string): HTMLElement => {
  const c = h('canvas.mz-key', { width: 28, height: 28 }) as HTMLCanvasElement;
  const ctx = c.getContext('2d');
  if (ctx) mark(ctx, shape, 14, 14, 8, color);
  return h('span.mz-legend-item', {}, c, label);
};

/**
 * The Furrows' HUD (docs/LABERINTOS.md, «Interfaz»): the party; what to do now (find the centre, work the lever, camp
 * or chest at hand, or back to a rope) with its progress; the torch, how much of the map the group has and its camps;
 * a map of the week behind a button (M on a keyboard); and the prowler, with an arrow from the edge when it is near.
 */
export class FurrowsHud {
  readonly el: HTMLElement;
  readonly party: PartyFrames;
  private goal = h('div.ex-goal.mz-goal', { role: 'status', 'aria-live': 'polite', 'data-test': 'maze-goal' });
  private goalTitle = h('b.ex-title');
  private goalText = h('p.ex-text');
  private barFill = h('i');
  private bar = h('div.dg-goal-bar.ex-bar', {}, this.barFill);
  private torch = h('span.ex-ink.mz-torch', { 'data-test': 'maze-torch' });
  private mapPct = h('span.mz-pct');
  private camps = h('span.mz-camps');
  private status = h('div.ex-status', {}, this.torch, this.mapPct, this.camps);
  private ways = h('div.dg-ways');
  private wayEls: HTMLElement[] = [];
  private mapBtn: HTMLButtonElement;
  /** Up the rope at hand: shown only by a rope. */
  private climbBtn: HTMLButtonElement;
  private mapBox: HTMLElement;
  private canvas: HTMLCanvasElement;
  private mapOpen = false;
  /** How the Furrows work, behind a «?» that pulses until it has been opened once. */
  private howBtn: HTMLButtonElement;
  private howBox: HTMLElement;
  private pings: (Way & { until: number })[] = [];
  /** The ways pointed at this frame (a tap on an arrow walks there). */
  private shown: Way[] = [];
  /** Where the camera looks, on the ground (world x, z): the map turns so that this is up. */
  private look = { x: 0, z: -1 };
  /** The map's last drawing, to read a tap on it back into the world. */
  private view = { k: 1, cos: 1, sin: 0, W: 1, mid: { x: 0, y: 0 } };
  private dir = new THREE.Vector3();
  private prowlTold = false;
  /** The panel folded to its title, bar and torch (a tap on its corner), so the maze shows. */
  private fold: GoalFold;
  private told = new Set<string>();
  private onKey = (e: KeyboardEvent): void => {
    if ((e.key === 'm' || e.key === 'M') && !(e.target instanceof HTMLInputElement) && !(e.target instanceof HTMLTextAreaElement)) this.toggleMap();
  };

  constructor(
    private s: () => BattleState,
    private me: string,
    names: Record<string, string>,
    private toast: (msg: string, ms?: number) => void,
    private send: (c: Command) => void = () => undefined,
  ) {
    this.party = new PartyFrames(me, names);
    this.fold = new GoalFold(this.goal, 'maze-fold');
    this.goal.append(this.fold.el, this.goalTitle, this.goalText, this.bar, this.status);
    this.mapBtn = h('button.iconbtn.mz-mapbtn', { 'aria-label': t('maze.map'), title: t('maze.map'), 'data-test': 'maze-map-btn', html: ICONS.map, onclick: () => this.toggleMap() }) as HTMLButtonElement;
    this.canvas = h('canvas.mz-canvas', { width: 640, height: 640, 'data-test': 'maze-canvas', onclick: (e: Event) => this.mapTap(e as MouseEvent) }) as HTMLCanvasElement;
    const legend = h(
      'div.mz-legend',
      {},
      legendItem('dot', MARK.me, t('maze.key.me')),
      legendItem('dot', MARK.ally, t('maze.key.ally')),
      legendItem('cross', MARK.down, t('maze.key.down')),
      legendItem('dot', MARK.entry, t('maze.key.entry')),
      legendItem('tent', MARK.camp, t('maze.key.camp')),
      legendItem('tent', MARK.campDone, t('maze.key.campDone')),
      legendItem('square', MARK.lever, t('maze.key.lever')),
      legendItem('diamond', MARK.chest, t('maze.key.chest')),
      legendItem('ring', MARK.down, t('maze.key.centre')),
    );
    this.mapBox = h(
      'div.mz-map',
      // A tap outside the card closes it; the button's click must not reach here too (it would open it again).
      { 'data-test': 'maze-map', hidden: true, onclick: (e: Event) => { if (e.target === this.mapBox) this.toggleMap(); } },
      h('div.mz-map-card', {}, h('b', {}, t('maze.mapTitle')), this.canvas, h('p.small', {}, t('maze.mapHint')), legend, h('button.btn.small.ghost', { 'data-test': 'maze-map-close', onclick: () => this.toggleMap() }, t('maze.mapClose'))),
    );
    const how = (k: string): HTMLElement => h('li', {}, h('b', {}, t(`maze.how.${k}`)), ' ', t(`maze.how.${k}.desc`));
    this.howBox = h(
      'div.mz-map.mz-how',
      { 'data-test': 'maze-how', hidden: true, onclick: (e: Event) => { if (e.target === this.howBox) this.howBox.hidden = true; } },
      h(
        'div.mz-map-card',
        {},
        h('b', {}, t('maze.how.title')),
        h('ul.mz-how-list', {}, how('goal'), how('lever'), how('camp'), how('chest'), how('rope'), how('torch'), how('down'), how('prowler')),
        h('button.btn.small.ghost', { 'data-test': 'maze-how-close', onclick: () => (this.howBox.hidden = true) }, t('maze.how.close')),
      ),
    );
    this.howBtn = h('button.iconbtn.mz-howbtn', { 'aria-label': t('maze.how.title'), title: t('maze.how.title'), 'data-test': 'maze-how-btn', onclick: () => this.openHow() }, '?') as HTMLButtonElement;
    try {
      if (!localStorage.getItem(HOW_SEEN)) this.howBtn.classList.add('pulse');
    } catch {
      /* private mode: no pulse */
    }
    this.climbBtn = h('button.btn.mz-climb', { 'data-test': 'maze-climb', hidden: true, onclick: () => this.send({ t: 'climb' }) }, t('maze.climb')) as HTMLButtonElement;
    this.el = h('div.dg-hud.ex-hud.mz-hud', {}, this.ways, this.party.el, this.goal, this.mapBtn, this.howBtn, this.climbBtn, this.mapBox, this.howBox);
    this.fold.follow(this.party.el);
    window.addEventListener('keydown', this.onKey);
  }

  private openHow(): void {
    this.howBox.hidden = false;
    this.howBtn.classList.remove('pulse');
    try {
      localStorage.setItem(HOW_SEEN, '1');
    } catch {
      /* fine */
    }
  }

  private toggleMap(): void {
    this.mapOpen = !this.mapOpen;
    this.mapBox.hidden = !this.mapOpen;
    if (this.mapOpen) this.drawMap();
  }

  /** The tick the run started on (the first one this HUD saw started). */
  private started = -1;
  private firstTick(s: BattleState): number {
    if (this.started < 0 && s.started) this.started = s.tick;
    return this.started < 0 ? s.tick : this.started;
  }

  private mine(s: BattleState): Hero | undefined {
    return s.heroes.find((x) => x.pid === this.me);
  }

  /** Once per sim tick. */
  update(): void {
    const s = this.s();
    const m = s.dungeon?.maze;
    if (!m) return;
    this.party.update(s);
    const me = this.mine(s);
    const [title, text, progress] = this.objective(s, m, me);
    this.goalTitle.textContent = title;
    this.goalText.textContent = text;
    this.fold.say(text);
    this.bar.hidden = progress === null;
    if (progress !== null) setFill(this.barFill, progress);
    this.goal.classList.toggle('show', s.started);
    const left = m.torchAt > 0 ? secs(m.torchAt - s.tick) : secs(MAZE.torchTicks);
    this.torch.innerHTML = `${ICONS.ember}${t('maze.torch')} <b>${clock(left)}</b>`;
    this.torch.classList.toggle('low', left <= 90);
    this.goal.classList.toggle('urgent', left <= 90 || this.prowlerNear(s, m, me));
    const seen = m.seen.filter(Boolean).length;
    this.mapPct.innerHTML = `${ICONS.map}${t('maze.mapPct', { p: Math.round((seen * 100) / m.layout.nodes.length) })}`;
    this.camps.innerHTML = `${ICONS.campaign}${t('maze.camps', { k: m.camps.filter(Boolean).length, n: m.camps.length })}`;
    this.el.classList.toggle('prowl', this.prowlerNear(s, m, me));
    this.climbBtn.hidden = !me || me.downTicks > 0 || !s.started || s.result !== 'running' || ropeAt(m, me) < 0 || (!m.armed && m.torchAt - s.tick > 12 * 90);
    if (this.mapOpen && s.tick % 6 === 0) this.drawMap();
    this.fallen(s);
  }

  private prowlerNear(s: BattleState, m: Maze, me: Hero | undefined): boolean {
    if (!me || me.downTicks > 0) return false;
    return m.prowlers.some((p) => {
      const e = s.enemies.find((x) => x.id === p.id);
      return !!e && p.awayUntil <= s.tick && e.scared === undefined && near(e, me, PROWL_NEAR);
    });
  }

  /** What to do now: the work at hand with its progress, the hall's fight, back to a rope, or finding the centre. */
  private objective(s: BattleState, m: Maze, me: Hero | undefined): [string, string, number | null] {
    const title = t('maze.goal.title');
    if (!me) return [title, '', null];
    if (me.downTicks > 0) return [title, t('maze.goal.down'), (me.revive ?? 0) > 0 ? (me.revive ?? 0) / DUNGEON.reviveTicks : null];
    const L = m.layout;
    const R = MAZE.workRange;
    for (let i = 0; i < L.levers.length; i++) if (!m.gates[i] && near(me, at(m, (L.levers[i] as { node: number }).node), R)) return [title, t('maze.goal.lever'), (m.leverHold[i] ?? 0) / MAZE.leverTicks];
    for (let i = 0; i < L.camps.length; i++) if (!m.camps[i] && near(me, at(m, L.camps[i] as number), R)) return [title, t('maze.goal.camp'), (m.campHold[i] ?? 0) / MAZE.campTicks];
    for (let i = 0; i < L.chests.length; i++) if (!m.chests[i] && near(me, at(m, L.chests[i] as number), R)) return [title, t('maze.goal.chest'), (m.chestHold[i] ?? 0) / MAZE.chestTicks];
    if (m.hallWoke && s.enemies.some((e) => e.id === m.guardian && e.hp > 0)) return [title, t('maze.goal.hall'), null];
    if (ropeAt(m, me) >= 0 && (m.armed || m.torchAt - s.tick < 12 * 90)) return [title, t('maze.goal.back'), null];
    // Just in: how to move, and where the way out is.
    if (!m.armed && s.tick < this.firstTick(s) + 12 * 20) return [title, t('maze.goal.start'), null];
    if (m.torchAt > 0 && secs(m.torchAt - s.tick) <= 90) return [title, t('maze.goal.torch'), null];
    return [title, m.centreWon ? t('maze.goal.done') : t('maze.goal.find'), null];
  }

  /** A word when a friend falls, once each time. */
  private fallen(s: BattleState): void {
    for (const hero of s.heroes) {
      const key = `down:${hero.pid}`;
      if (hero.downTicks > 0 && hero.pid !== this.me && !this.told.has(key)) {
        this.told.add(key);
        this.toast(t('maze.ev.fallen', { name: this.party.nameOf(hero) }), 3200);
      } else if (hero.downTicks === 0) this.told.delete(key);
    }
  }

  private ping(px: number, py: number, label: string, ms = 7000): void {
    this.pings.push({ x: px, y: py, label, until: performance.now() + ms });
  }

  /** Every tick's events, said to everyone as what they change («gate open: it stays that way for everyone»). */
  events(s: BattleState): void {
    const m = s.dungeon?.maze;
    if (!m) return;
    const me = this.mine(s);
    for (const e of s._events ?? []) {
      if (e.t === 'boom' && e.kind === 'prowlerAway') this.toast(t('maze.ev.prowlerAway'), 2800);
      if (e.t !== 'skill') continue;
      if (e.skill === 'mazeGate') {
        this.toast(t('maze.ev.gate'), 4200);
        this.ping(e.x, e.y, t('maze.ev.gate').split(':')[0] ?? '');
      } else if (e.skill === 'mazeCamp') this.toast(t('maze.ev.camp'), 4200);
      else if (e.skill === 'mazeChest') this.toast(t('maze.ev.chest'), 3600);
      else if (e.skill === 'mazeHall') this.toast(t('maze.ev.hall'), 4200);
      else if (e.skill === 'mazeClimbWait') this.toast(t('maze.ev.climbWait'), 3600);
    }
    const close = this.prowlerNear(s, m, me);
    if (close && !this.prowlTold) this.toast(t('maze.ev.prowler'), 2600);
    this.prowlTold = close;
  }

  /** Where to point: the nearest rope when the torch is low, what just opened, and the prowler when it is near. */
  private targets(s: BattleState): Way[] {
    const m = s.dungeon?.maze;
    const me = this.mine(s);
    if (!m || !me || me.downTicks > 0 || !s.started || s.result !== 'running') return [];
    const now = performance.now();
    this.pings = this.pings.filter((p) => p.until > now);
    const out: Way[] = this.pings.slice(-1);
    // Just in: the rope out is marked, so the way back is known before going anywhere.
    if (!m.armed && s.tick < this.firstTick(s) + 12 * 20) out.push({ ...at(m, m.start), label: t('maze.way.ropeOut') });
    if (m.torchAt > 0 && secs(m.torchAt - s.tick) <= 90) {
      const ropes = [m.layout.entry, ...m.layout.camps.filter((_, i) => m.camps[i])];
      let best: { x: number; y: number } | null = null;
      let bd = Infinity;
      for (const r of ropes) {
        const p = at(m, r);
        const d = (p.x - me.x) ** 2 + (p.y - me.y) ** 2;
        if (d < bd) {
          bd = d;
          best = p;
        }
      }
      if (best) out.push({ ...best, label: t('maze.way.rope') });
    }
    // A friend on the floor: where to go to lift them.
    for (const hero of s.heroes) if (hero.pid !== this.me && hero.downTicks > 0) out.push({ x: hero.x, y: hero.y, label: t('maze.way.lift', { name: this.party.nameOf(hero) }), ally: true });
    // The centre's way, discreetly, while it is off screen.
    if (!m.centreWon) out.push({ ...at(m, m.layout.hall), label: t('maze.way.centre'), edgeOnly: true });
    if (this.prowlerNear(s, m, me)) {
      const p = m.prowlers.map((x) => s.enemies.find((e) => e.id === x.id)).find((e) => !!e && near(e, me, PROWL_NEAR));
      if (p) out.push({ x: p.x, y: p.y, label: t('maze.way.prowler'), danger: true });
    }
    return out;
  }

  /** Every animation frame: the arrows, over each place in view or held at the edge pointing to it. */
  place(positions: Map<number, THREE.Vector3>, camera: THREE.Camera): void {
    const s = this.s();
    const mine = this.mine(s);
    const mp = mine ? positions.get(mine.id) : undefined;
    const ways = this.targets(s);
    this.shown = ways;
    camera.getWorldDirection(this.dir);
    if (Math.hypot(this.dir.x, this.dir.z) > 1e-3) this.look = { x: this.dir.x, z: this.dir.z };
    const W = viewW();
    const H = viewH();
    const v = new THREE.Vector3();
    ways.forEach((w, k) => {
      let el = this.wayEls[k];
      if (!el) {
        // A tap on the arrow walks there (never toward the prowler).
        el = h('button.dg-way', { type: 'button', onclick: () => this.walkTo(this.shown[k]) }, h('span.dg-way-arrow', { html: CHEVRON }), h('span.dg-way-txt'));
        this.ways.append(el);
        this.wayEls[k] = el;
      }
      el.classList.toggle('danger', !!w.danger);
      el.classList.toggle('ally', !!w.ally);
      const p = simToWorld(w.x, w.y);
      const close = !!mp && Math.hypot(mp.x - p.x, mp.z - p.z) < 2;
      el.hidden = close && !w.danger && !w.ally && w.label !== t('maze.way.ropeOut');
      if (el.hidden) return;
      (el.querySelector('.dg-way-txt') as HTMLElement).textContent = w.label;
      v.copy(p).setY(1.4).project(camera);
      const behind = v.z > 1;
      let px = (v.x * 0.5 + 0.5) * W;
      let py = (-v.y * 0.5 + 0.5) * H;
      if (behind) {
        px = W - px;
        py = H - py;
      }
      const m = 46;
      const top = W <= 640 ? 200 : 120;
      const inView = !behind && px > m && px < W - m && py > top && py < H - m;
      el.classList.toggle('edge', !inView);
      el.classList.toggle('soft', !!w.edgeOnly);
      if (w.edgeOnly && inView) {
        el.hidden = true;
        return;
      }
      let turn = 0;
      if (!inView) {
        const cx = W / 2;
        const cy = (top + H - m) / 2;
        const dx = px - cx;
        const dy = py - cy;
        const k2 = Math.min((W / 2 - m) / Math.max(1, Math.abs(dx)), (H - m - top) / 2 / Math.max(1, Math.abs(dy)));
        px = cx + dx * k2;
        py = cy + dy * k2;
        turn = Math.atan2(dy, dx) - Math.PI / 2;
      }
      el.style.transform = `translate(${px.toFixed(1)}px, ${py.toFixed(1)}px) translate(-50%, -50%)`;
      (el.querySelector('.dg-way-arrow') as HTMLElement).style.transform = `rotate(${turn.toFixed(3)}rad)`;
    });
    for (let k = ways.length; k < this.wayEls.length; k++) (this.wayEls[k] as HTMLElement).hidden = true;
  }

  /** The week's map, turned so that where the camera looks is up: corridors between seen crossings, gates, camps, chests, the centre if seen, and the party. */
  private drawMap(): void {
    const s = this.s();
    const m = s.dungeon?.maze;
    const ctx = this.canvas.getContext('2d');
    if (!m || !ctx) return;
    const L = m.layout;
    const W = this.canvas.width;
    // Framed on what the group knows (and where the party is), not on the whole maze: big enough to follow.
    const known = [...L.nodes.filter((_, i) => m.seen[i]).map((n) => hexToWorld(n.q, n.r)), ...s.heroes.map((x) => ({ x: x.x, y: x.y }))];
    let x0 = Infinity;
    let x1 = -Infinity;
    let y0 = Infinity;
    let y1 = -Infinity;
    for (const p of known) {
      x0 = Math.min(x0, p.x);
      x1 = Math.max(x1, p.x);
      y0 = Math.min(y0, p.y);
      y1 = Math.max(y1, p.y);
    }
    const mid = known.length ? { x: (x0 + x1) / 2, y: (y0 + y1) / 2 } : { x: 0, y: 0 };
    let ext = MAP_MIN_EXT;
    for (const p of known) ext = Math.max(ext, Math.hypot(p.x - mid.x, p.y - mid.y));
    const k = (W / 2 - 40) / ext;
    // Sim x, y lie on world x, z: turn the look direction to point up the canvas.
    const a = -Math.PI / 2 - Math.atan2(this.look.z, this.look.x);
    const cos = Math.cos(a);
    const sin = Math.sin(a);
    this.view = { k, cos, sin, W, mid };
    const px = (p: { x: number; y: number }): [number, number] => {
      const dx = p.x - mid.x;
      const dy = p.y - mid.y;
      return [W / 2 + (dx * cos - dy * sin) * k, W / 2 + (dx * sin + dy * cos) * k];
    };
    ctx.fillStyle = '#0d0f13';
    ctx.fillRect(0, 0, W, W);
    ctx.lineCap = 'round';
    L.edges.forEach((e, i) => {
      if (!m.seen[e.a] || !m.seen[e.b]) return;
      const gate = L.levers.findIndex((l) => l.edge === i);
      ctx.strokeStyle = e.kind === 'gate' ? (m.gates[gate] ? '#ff8a6e' : '#6b2a22') : e.kind === 'breath' ? '#6b6f78' : '#c8ccd4';
      ctx.lineWidth = e.kind === 'path' ? 7 : 5;
      ctx.setLineDash(e.kind === 'breath' ? [5, 7] : []);
      const [ax, ay] = px(at(m, e.a));
      const [bx, by] = px(at(m, e.b));
      ctx.beginPath();
      ctx.moveTo(ax, ay);
      ctx.lineTo(bx, by);
      ctx.stroke();
    });
    ctx.setLineDash([]);
    const put = (i: number, shape: Shape, color: string, r: number): void => {
      const [x, y] = px(at(m, i));
      mark(ctx, shape, x, y, r, color);
    };
    L.chests.forEach((c, i) => m.seen[c] && put(c, 'diamond', m.chests[i] ? MARK.chestDone : MARK.chest, 8));
    L.camps.forEach((c, i) => m.seen[c] && put(c, 'tent', m.camps[i] ? MARK.campDone : MARK.camp, 10));
    L.levers.forEach((l, i) => m.seen[l.node] && put(l.node, 'square', m.gates[i] ? MARK.leverDone : MARK.lever, 7));
    put(L.entry, 'dot', MARK.entry, 9);
    if (m.seen[L.hall]) {
      const [x, y] = px(at(m, L.hall));
      ctx.strokeStyle = '#ff4f3a';
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.arc(x, y, 3 * k * 1000 * 0.9, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.font = '600 22px "Alegreya Sans", system-ui, sans-serif';
    ctx.textBaseline = 'middle';
    // The others first, each with their name (a cross when they are down); then me, on top, with a ring.
    for (const hero of s.heroes) {
      if (hero.pid === this.me) continue;
      const [x, y] = px(hero);
      const down = hero.downTicks > 0;
      mark(ctx, down ? 'cross' : 'dot', x, y, down ? 9 : 8, down ? MARK.down : MARK.ally);
      ctx.fillStyle = down ? MARK.down : MARK.ally;
      ctx.lineWidth = 4;
      ctx.strokeStyle = '#0d0f13';
      const name = this.party.nameOf(hero);
      ctx.strokeText(name, x + 14, y);
      ctx.fillText(name, x + 14, y);
    }
    const mine = this.mine(s);
    if (mine) {
      const [x, y] = px(mine);
      mark(ctx, 'ring', x, y, 16, 'rgba(255, 255, 255, 0.55)');
      ctx.strokeStyle = '#0d0f13';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(x, y, 10, 0, Math.PI * 2);
      ctx.stroke();
      mark(ctx, 'dot', x, y, 9, MARK.me);
      this.canvas.dataset.me = `${(x / W).toFixed(3)},${(y / W).toFixed(3)}`;
    }
  }

  /** A tap on the map: walk to the known crossing nearest to it. */
  private mapTap(e: MouseEvent): void {
    const s = this.s();
    const m = s.dungeon?.maze;
    if (!m) return;
    const r = this.canvas.getBoundingClientRect();
    const { k, cos, sin, W, mid } = this.view;
    const cx = ((e.clientX - r.left) / r.width) * W - W / 2;
    const cy = ((e.clientY - r.top) / r.height) * W - W / 2;
    // Undo the turn, then the scale.
    const x = mid.x + (cx * cos + cy * sin) / k;
    const y = mid.y + (-cx * sin + cy * cos) / k;
    let best = -1;
    let bd = Infinity;
    m.layout.nodes.forEach((_, i) => {
      if (!m.seen[i]) return;
      const p = at(m, i);
      const d = (p.x - x) ** 2 + (p.y - y) ** 2;
      if (d < bd) {
        bd = d;
        best = i;
      }
    });
    // Close enough to a crossing on the map (about a finger's width on a phone).
    if (best < 0 || Math.sqrt(bd) * k > 120) return;
    this.walkTo(at(m, best));
    this.toggleMap();
  }

  /** Walks my hero there (an arrow or the map), as a tap on the floor would; never toward the prowler. */
  private walkTo(w: { x: number; y: number; danger?: boolean } | undefined): void {
    if (!w || w.danger) return;
    this.send({ t: 'move', x: w.x, y: w.y });
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKey);
  }
}
